import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import observabilidadApiService from '../services/ObservabilidadApiService';
import LoadingSpinner from './common/LoadingSpinner';
import TableExportActions from './common/TableExportActions';
import { isAdminColegio } from '../utils/adminMenu';
import useSystemErrorsRealtime, {
  errorsEventMatchesColegio,
  errorsEventMatchesParticular,
  errorsEventMatchesTenantType
} from '../hooks/useSystemErrorsRealtime';
import './AdminColegioStats.css';

const formatDateInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayRange = () => {
  const now = new Date();
  const value = formatDateInput(now);
  return { from: value, to: value };
};

const getMonthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: formatDateInput(from), to: formatDateInput(now) };
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateLabel = (value) => {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const truncate = (value, maxLength = 120) => {
  if (!value) return '—';
  return value.length <= maxLength ? value : `${value.slice(0, maxLength)}…`;
};

const formatTenantTypeLabel = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (normalized === 'particulares') return 'Particulares';
  if (normalized === 'colegios') return 'Colegios';
  return 'Global';
};

const getTenantEntityName = (evento) => evento?.particularNombre || evento?.colegioNombre || '—';

const ROLE_LABELS = {
  admin: 'Admin general',
  admincolegio: 'Admin colegio',
  adminarea: 'Admin área',
  profesor: 'Profesor',
  alumno: 'Alumno',
  familiar: 'Familiar',
  profesorparticular: 'Profesor particular',
  alumnoparticular: 'Alumno particular',
  responsableparticular: 'Responsable particular'
};

const formatRoleLabel = (evento) => {
  const raw = (evento?.nombreRol || evento?.rolNormalizado || '').toString().trim();
  if (!raw) return '—';
  return ROLE_LABELS[raw.toLowerCase().replace(/[\s_-]/g, '')] || raw;
};

const getUserLabel = (evento) => evento?.nombreUsuario || evento?.emailUsuario || '—';

const isObservabilitySelfEvent = (payload) => {
  const path = (payload?.path || '').toString().trim().toLowerCase();
  return path.startsWith('/api/observabilidad/errores-generales');
};

const AdminGeneralErrors = () => {
  const navigate = useNavigate();
  const { user, logout, isLoading: authLoading } = useAuth();
  const [selectedPreset, setSelectedPreset] = useState('month');
  const [filters, setFilters] = useState(() => getMonthRange());
  const [selectedTipoTenant, setSelectedTipoTenant] = useState('todos');
  const [selectedColegioId, setSelectedColegioId] = useState('');
  const [selectedParticularId, setSelectedParticularId] = useState('');
  const [handledFilter, setHandledFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPurgingExpired, setIsPurgingExpired] = useState(false);
  const [isForcePurging, setIsForcePurging] = useState(false);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [manualPurgeRange, setManualPurgeRange] = useState({ from: '', to: '' });

  const isGeneralAdmin = useMemo(() => !isAdminColegio(user), [user]);
  const roleLabel = user?.roleName || user?.nombreRol || user?.role || 'Admin';
  const colegios = Array.isArray(data?.colegios) ? data.colegios : [];
  const particulares = Array.isArray(data?.particulares) ? data.particulares : [];
  const selectedColegioLabel = data?.colegioNombre || 'Todos los colegios';
  const selectedParticularLabel = data?.particularNombre || 'Todas las cuentas particulares';
  const selectedTipoTenantLabel = selectedTipoTenant === 'particulares'
    ? 'Particulares'
    : selectedTipoTenant === 'colegios'
      ? 'Colegios'
      : 'Todos';
  const selectedScopeLabel = selectedColegioId
    ? selectedColegioLabel
    : selectedParticularId
      ? selectedParticularLabel
      : selectedTipoTenantLabel;

  const loadData = async ({
    nextFilters = filters,
    nextTipoTenant = selectedTipoTenant,
    nextColegioId = selectedColegioId,
    nextParticularId = selectedParticularId,
    nextHandled = handledFilter,
    nextLevel = levelFilter,
    nextSearch = searchTerm,
    showLoader = true,
    silentError = false
  } = {}) => {
    if (showLoader) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }

    if (!silentError) {
      setError('');
    }

    try {
      const effectiveColegioId = nextTipoTenant === 'particulares' ? '' : nextColegioId;
      const effectiveParticularId = nextTipoTenant === 'colegios' || effectiveColegioId ? '' : nextParticularId;
      const response = await observabilidadApiService.getGlobalErrors({
        ...nextFilters,
        colegioId: effectiveColegioId || undefined,
        profesorParticularId: effectiveParticularId || undefined,
        tipoTenant: nextTipoTenant,
        handled: nextHandled,
        nivel: nextLevel,
        search: nextSearch || undefined
      });
      setData(response);
      if (!silentError) {
        setError('');
      }
      return response;
    } catch (err) {
      if (!silentError) {
        setError(err.message || 'No pudimos cargar la observabilidad de errores.');
        setData(null);
      } else {
        console.warn('No se pudieron refrescar los errores en vivo', err);
      }
      return null;
    } finally {
      if (showLoader) {
        setIsLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  const { highlightedCards, liveAlert } = useSystemErrorsRealtime({
    enabled: !authLoading && isGeneralAdmin && !!data && !error,
    colegioId: selectedTipoTenant === 'particulares' ? '' : selectedColegioId,
    data,
    loadData,
    shouldHandleEvent: (payload) =>
      !isObservabilitySelfEvent(payload) &&
      errorsEventMatchesTenantType(payload, selectedTipoTenant) &&
      errorsEventMatchesParticular(payload, selectedTipoTenant === 'colegios' ? '' : selectedParticularId) &&
      errorsEventMatchesColegio(payload, selectedTipoTenant === 'particulares' ? '' : selectedColegioId)
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isGeneralAdmin) {
      setError('No tienes permisos para acceder a esta sección.');
      setIsLoading(false);
      return;
    }
    loadData({ nextFilters: filters, nextTipoTenant: selectedTipoTenant, nextColegioId: selectedColegioId });
  }, [authLoading, isGeneralAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (preset) => {
    const nextFilters = preset === 'today' ? getTodayRange() : getMonthRange();
    setSelectedPreset(preset);
    setFilters(nextFilters);
    loadData({
      nextFilters,
      nextTipoTenant: selectedTipoTenant,
      nextColegioId: selectedColegioId,
      nextHandled: handledFilter,
      nextLevel: levelFilter,
      nextSearch: searchTerm
    });
  };

  const handleTenantTypeChange = (value) => {
    setSelectedTipoTenant(value);
    if (value === 'particulares') {
      setSelectedColegioId('');
    } else if (value === 'colegios') {
      setSelectedParticularId('');
    }
  };

  const handleColegioChange = (value) => {
    setSelectedColegioId(value);
    if (value) {
      setSelectedTipoTenant('colegios');
      setSelectedParticularId('');
    }
  };

  const handleParticularChange = (value) => {
    setSelectedParticularId(value);
    if (value) {
      setSelectedTipoTenant('particulares');
      setSelectedColegioId('');
    }
  };

  const handleCustomFilterChange = (field, value) => {
    setSelectedPreset('custom');
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setActionMessage('');
    loadData();
  };

  const handlePurgeExpired = async () => {
    const confirmed = window.confirm(
      'Esto exportará a TXT y eliminará de la base sólo los errores vencidos por retención. ¿Continuar?'
    );

    if (!confirmed) return;

    setIsPurgingExpired(true);
    setActionMessage('');
    setError('');
    try {
      const response = await observabilidadApiService.purgeExpiredErrors();
      setActionMessage(response?.message || 'Purga ejecutada.');
      await loadData({ showLoader: false, silentError: false });
    } catch (err) {
      setError(err.message || 'No se pudo ejecutar la purga de errores vencidos.');
    } finally {
      setIsPurgingExpired(false);
    }
  };

  const handleManualPurgeChange = (field, value) => {
    setManualPurgeRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearManualPurgeRange = () => {
    setManualPurgeRange({ from: '', to: '' });
  };

  const buildManualPurgeConfirmText = () => {
    if (manualPurgeRange.from && manualPurgeRange.to) {
      return `Esto exportará a TXT y eliminará de la base todos los errores entre ${manualPurgeRange.from} y ${manualPurgeRange.to}. ¿Continuar?`;
    }
    if (manualPurgeRange.from) {
      return `Esto exportará a TXT y eliminará de la base todos los errores desde ${manualPurgeRange.from} en adelante. ¿Continuar?`;
    }
    if (manualPurgeRange.to) {
      return `Esto exportará a TXT y eliminará de la base todos los errores hasta ${manualPurgeRange.to}. ¿Continuar?`;
    }
    return 'Esto exportará a TXT y eliminará de la base todos los errores registrados. ¿Continuar?';
  };

  const handleForcePurge = async () => {
    if (manualPurgeRange.from && manualPurgeRange.to && manualPurgeRange.from > manualPurgeRange.to) {
      setError('El rango manual de purga es inválido.');
      return;
    }

    const confirmed = window.confirm(buildManualPurgeConfirmText());
    if (!confirmed) return;

    setIsForcePurging(true);
    setActionMessage('');
    setError('');
    try {
      const response = await observabilidadApiService.forcePurgeErrors({
        from: manualPurgeRange.from || undefined,
        to: manualPurgeRange.to || undefined
      });
      setActionMessage(response?.message || 'Purgado manual ejecutado.');
      await loadData({ showLoader: false, silentError: false });
    } catch (err) {
      setError(err.message || 'No se pudo ejecutar el purgado manual.');
    } finally {
      setIsForcePurging(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (authLoading || isLoading) {
    return (
      <div className="colegio-stats-page">
        <div className="colegio-stats-loading">
          <LoadingSpinner message="Cargando observabilidad de errores..." />
        </div>
      </div>
    );
  }

  return (
    <div className="colegio-stats-page">
      <div className="colegio-stats-shell">
        <header className="colegio-stats-header">
          <div>
            <p className="eyebrow">Errores generales</p>
            <h1>Observabilidad de fallas</h1>
            <p className="subtitle">
              {selectedColegioId
                ? `${selectedColegioLabel}. Vista global filtrada para ese colegio.`
                : selectedParticularId
                  ? `${selectedParticularLabel}. Vista global filtrada para esa cuenta particular.`
                  : `Errores del backend para ${selectedTipoTenantLabel.toLowerCase()} agrupados y actualizados en tiempo real.`}
            </p>
            <p className="subtitle">
              Retención: {Number(data?.retentionDays || 90)} días. Purga diaria {String(data?.purgeHourLocal ?? 3).padStart(2, '0')}:{String(data?.purgeMinuteLocal ?? 15).padStart(2, '0')}.
            </p>
          </div>
          <div className="colegio-stats-header-actions">
            <div className="colegio-stats-user-chip">
              <span>{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <small>{roleLabel}</small>
            </div>
            <button type="button" className="secondary-btn" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left" />
              <span>Volver</span>
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={handlePurgeExpired}
              disabled={isPurgingExpired || isForcePurging}
            >
              <i className="fas fa-broom" />
              <span>{isPurgingExpired ? 'Purgando…' : 'Purgar vencidos'}</span>
            </button>
            <button type="button" className="logout-btn-header" onClick={handleLogout} title="Cerrar sesión">
              <i className="fas fa-sign-out-alt" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </header>

        <section className="colegio-stats-toolbar">
          <div className="colegio-stats-presets">
            <button
              type="button"
              className={selectedPreset === 'today' ? 'active' : ''}
              onClick={() => applyPreset('today')}
            >
              Hoy
            </button>
            <button
              type="button"
              className={selectedPreset === 'month' ? 'active' : ''}
              onClick={() => applyPreset('month')}
            >
              Mes actual
            </button>
          </div>
          <div className="colegio-stats-custom-range">
            <label>
              Tipo
              <select value={selectedTipoTenant} onChange={(e) => handleTenantTypeChange(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="colegios">Colegios</option>
                <option value="particulares">Particulares</option>
              </select>
            </label>
            <label>
              Colegio
              <select
                value={selectedColegioId}
                onChange={(e) => handleColegioChange(e.target.value)}
                disabled={selectedTipoTenant === 'particulares'}
              >
                <option value="">Todos los colegios</option>
                {colegios.map((colegio) => (
                  <option key={colegio.id} value={colegio.id}>
                    {colegio.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Cuenta particular
              <select
                value={selectedParticularId}
                onChange={(e) => handleParticularChange(e.target.value)}
                disabled={selectedTipoTenant === 'colegios'}
              >
                <option value="">Todas las cuentas</option>
                {particulares.map((particular) => (
                  <option key={particular.id} value={particular.id}>
                    {particular.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Estado
              <select value={handledFilter} onChange={(e) => setHandledFilter(e.target.value)}>
                <option value="all">Todos</option>
                <option value="unhandled">No manejados</option>
                <option value="handled">Manejados</option>
              </select>
            </label>
            <label>
              Nivel
              <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
                <option value="all">Todos</option>
                <option value="error">Error</option>
                <option value="critical">Critical</option>
              </select>
            </label>
            <label>
              Buscar
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Mensaje, categoría, endpoint"
              />
            </label>
            <label>
              Desde
              <input
                type="date"
                value={filters.from}
                onChange={(e) => handleCustomFilterChange('from', e.target.value)}
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={filters.to}
                onChange={(e) => handleCustomFilterChange('to', e.target.value)}
              />
            </label>
            <button type="button" className="primary-btn" onClick={handleApplyFilters}>
              <i className="fas fa-filter" />
              <span>Aplicar filtros</span>
            </button>
          </div>
        </section>

        <section className="colegio-stats-toolbar">
          <div>
            <p className="eyebrow">Purgado manual</p>
            <p className="subtitle">Rango opcional. Si no completas fechas, purga todo lo registrado y lo archiva en TXT.</p>
          </div>
          <div className="colegio-stats-custom-range">
            <label>
              Desde
              <input
                type="date"
                value={manualPurgeRange.from}
                onChange={(e) => handleManualPurgeChange('from', e.target.value)}
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={manualPurgeRange.to}
                onChange={(e) => handleManualPurgeChange('to', e.target.value)}
              />
            </label>
            <button
              type="button"
              className="secondary-btn"
              onClick={handleClearManualPurgeRange}
              disabled={isForcePurging || isPurgingExpired}
            >
              <i className="fas fa-eraser" />
              <span>Limpiar rango</span>
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={handleForcePurge}
              disabled={isForcePurging || isPurgingExpired}
            >
              <i className="fas fa-trash-alt" />
              <span>{isForcePurging ? 'Purgando…' : 'Forzar purgado'}</span>
            </button>
          </div>
        </section>

        {liveAlert && (
          <div className="colegio-stats-live-alert">
            <i className="fas fa-bug" />
            <span>{liveAlert.text}</span>
            {isRefreshing && <small>Sincronizando…</small>}
          </div>
        )}

        {actionMessage && !error && (
          <div className="colegio-stats-live-alert">
            <i className="fas fa-check-circle" />
            <span>{actionMessage}</span>
          </div>
        )}

        {error && (
          <div className="colegio-stats-error">
            <i className="fas fa-exclamation-triangle" />
            <span>{error}</span>
          </div>
        )}

        {!error && (
          <>
            <section className="colegio-stats-cards">
              <article className={`colegio-stats-card${highlightedCards.totalErrores ? ' is-updated' : ''}`}>
                <p>Total de errores</p>
                <strong>{Number(data?.totalErrores || 0).toLocaleString('es-AR')}</strong>
                <span>{formatDateLabel(data?.from)} al {formatDateLabel(data?.to)}</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalCriticos ? ' is-updated' : ''}`}>
                <p>Errores críticos</p>
                <strong>{Number(data?.totalCriticos || 0).toLocaleString('es-AR')}</strong>
                <span>Eventos con nivel `critical`</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalNoManejados ? ' is-updated' : ''}`}>
                <p>No manejados</p>
                <strong>{Number(data?.totalNoManejados || 0).toLocaleString('es-AR')}</strong>
                <span>Excepciones capturadas por middleware</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalManejados ? ' is-updated' : ''}`}>
                <p>Manejados</p>
                <strong>{Number(data?.totalManejados || 0).toLocaleString('es-AR')}</strong>
                <span>Errores registrados vía logger</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.cantidadColegiosAfectados ? ' is-updated' : ''}`}>
                <p>Colegios afectados</p>
                <strong>{Number(data?.cantidadColegiosAfectados || 0).toLocaleString('es-AR')}</strong>
                <span>{selectedColegioId ? selectedColegioLabel : 'Con errores en el rango'}</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.cantidadParticularesAfectados ? ' is-updated' : ''}`}>
                <p>Particulares afectados</p>
                <strong>{Number(data?.cantidadParticularesAfectados || 0).toLocaleString('es-AR')}</strong>
                <span>Con errores en el rango</span>
              </article>
              <article className={`colegio-stats-card${highlightedCards.totalProblemasUnicos ? ' is-updated' : ''}`}>
                <p>Problemas únicos</p>
                <strong>{Number(data?.totalProblemasUnicos || 0).toLocaleString('es-AR')}</strong>
                <span>Fingerprints distintos</span>
              </article>
            </section>

            <section className="colegio-stats-grid">
              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Errores por nivel</h2>
                  <p>Distribución del volumen por severidad.</p>
                  <TableExportActions
                    tableSelector="#admin-general-errors-por-nivel-table"
                    fileName="errores-por-nivel"
                    title={`Errores por nivel - ${selectedScopeLabel}`}
                    compact
                    disabled={!Array.isArray(data?.resumenPorNivel) || data.resumenPorNivel.length === 0}
                  />
                </div>
                {Array.isArray(data?.resumenPorNivel) && data.resumenPorNivel.length > 0 ? (
                  <div className="panel-table-wrapper">
                    <table id="admin-general-errors-por-nivel-table" className="panel-table">
                      <thead>
                        <tr>
                          <th>Nivel</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.resumenPorNivel.map((item) => (
                          <tr key={item.nivel}>
                            <td>{item.nivel}</td>
                            <td className="text-right">{Number(item.total || 0).toLocaleString('es-AR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="panel-empty">No hay errores para el rango seleccionado.</div>
                )}
              </div>

              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Errores por origen</h2>
                  <p>Desde qué capa se están capturando.</p>
                  <TableExportActions
                    tableSelector="#admin-general-errors-por-origen-table"
                    fileName="errores-por-origen"
                    title={`Errores por origen - ${selectedScopeLabel}`}
                    compact
                    disabled={!Array.isArray(data?.resumenPorOrigen) || data.resumenPorOrigen.length === 0}
                  />
                </div>
                {Array.isArray(data?.resumenPorOrigen) && data.resumenPorOrigen.length > 0 ? (
                  <div className="panel-table-wrapper">
                    <table id="admin-general-errors-por-origen-table" className="panel-table">
                      <thead>
                        <tr>
                          <th>Origen</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.resumenPorOrigen.map((item) => (
                          <tr key={item.origen}>
                            <td>{item.origen}</td>
                            <td className="text-right">{Number(item.total || 0).toLocaleString('es-AR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="panel-empty">No hay orígenes con errores para mostrar.</div>
                )}
              </div>
            </section>

            <section className="colegio-stats-grid">
              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Problemas recurrentes</h2>
                  <p>Fingerprints con mayor cantidad de ocurrencias.</p>
                  <TableExportActions
                    tableSelector="#admin-general-errors-problemas-recurrentes-table"
                    fileName="errores-problemas-recurrentes"
                    title={`Problemas recurrentes - ${selectedScopeLabel}`}
                    compact
                    disabled={!Array.isArray(data?.topProblemas) || data.topProblemas.length === 0}
                  />
                </div>
                {Array.isArray(data?.topProblemas) && data.topProblemas.length > 0 ? (
                  <div className="panel-table-wrapper">
                    <table id="admin-general-errors-problemas-recurrentes-table" className="panel-table">
                      <thead>
                        <tr>
                          <th>Problema</th>
                          <th>Endpoint</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topProblemas.map((item) => (
                          <tr key={`${item.fingerprint}-${item.path || 'none'}`}>
                            <td>
                              <strong>{truncate(item.exceptionType || item.mensaje, 64)}</strong>
                              <p>{truncate(item.mensaje, 96)}</p>
                            </td>
                            <td>{item.path || item.categoria || '—'}</td>
                            <td className="text-right">{Number(item.total || 0).toLocaleString('es-AR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="panel-empty">No hay fingerprints recurrentes para mostrar.</div>
                )}
              </div>

              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Actividad por día</h2>
                  <p>Evolución del total, críticos y no manejados.</p>
                </div>
                {Array.isArray(data?.actividadPorDia) && data.actividadPorDia.length > 0 ? (
                  <div className="activity-list">
                    {data.actividadPorDia.map((item) => (
                      <div className="activity-row" key={item.fecha}>
                        <div>
                          <strong>{formatDateLabel(item.fecha)}</strong>
                          <p>{Number(item.total || 0).toLocaleString('es-AR')} errores</p>
                        </div>
                        <div className="activity-tags">
                          <span className="activity-tag">
                            Critical: {Number(item.criticos || 0).toLocaleString('es-AR')}
                          </span>
                          <span className="activity-tag">
                            No manejados: {Number(item.noManejados || 0).toLocaleString('es-AR')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="panel-empty">No hay actividad diaria disponible.</div>
                )}
              </div>
            </section>

            <section className="colegio-stats-grid">
              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Tipos de excepción</h2>
                  <p>Las clases de error más frecuentes.</p>
                  <TableExportActions
                    tableSelector="#admin-general-errors-tipos-excepcion-table"
                    fileName="errores-tipos-excepcion"
                    title={`Tipos de excepción - ${selectedScopeLabel}`}
                    compact
                    disabled={!Array.isArray(data?.topExceptionTypes) || data.topExceptionTypes.length === 0}
                  />
                </div>
                {Array.isArray(data?.topExceptionTypes) && data.topExceptionTypes.length > 0 ? (
                  <div className="panel-table-wrapper">
                    <table id="admin-general-errors-tipos-excepcion-table" className="panel-table">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topExceptionTypes.map((item) => (
                          <tr key={item.exceptionType}>
                            <td>{item.exceptionType}</td>
                            <td className="text-right">{Number(item.total || 0).toLocaleString('es-AR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="panel-empty">No hay tipos de excepción para mostrar.</div>
                )}
              </div>

              <div className="colegio-stats-panel">
                <div className="panel-header">
                  <h2>Endpoints más afectados</h2>
                  <p>Rutas con mayor cantidad de errores.</p>
                  <TableExportActions
                    tableSelector="#admin-general-errors-endpoints-table"
                    fileName="errores-endpoints-afectados"
                    title={`Endpoints más afectados - ${selectedScopeLabel}`}
                    compact
                    disabled={!Array.isArray(data?.topEndpoints) || data.topEndpoints.length === 0}
                  />
                </div>
                {Array.isArray(data?.topEndpoints) && data.topEndpoints.length > 0 ? (
                  <div className="panel-table-wrapper">
                    <table id="admin-general-errors-endpoints-table" className="panel-table">
                      <thead>
                        <tr>
                          <th>Endpoint</th>
                          <th className="text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topEndpoints.map((item) => (
                          <tr key={item.path}>
                            <td>{item.path}</td>
                            <td className="text-right">{Number(item.total || 0).toLocaleString('es-AR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="panel-empty">No hay endpoints para mostrar.</div>
                )}
              </div>
            </section>

            <section className="colegio-stats-panel">
              <div className="panel-header">
                <h2>Últimos errores</h2>
                <p>Feed reciente de errores registrados para el filtro actual.</p>
                <TableExportActions
                  tableSelector="#admin-general-errors-ultimos-table"
                  fileName="errores-ultimos"
                  title={`Últimos errores - ${selectedScopeLabel}`}
                  compact
                  disabled={!Array.isArray(data?.eventos) || data.eventos.length === 0}
                />
              </div>
              {Array.isArray(data?.eventos) && data.eventos.length > 0 ? (
                <div className="panel-table-wrapper">
                  <table id="admin-general-errors-ultimos-table" className="panel-table">
                    <thead>
                      <tr>
                        <th>Fecha local</th>
                        <th>Tipo</th>
                        <th>Entidad</th>
                        <th>Usuario</th>
                        <th>Rol</th>
                        <th>Nivel</th>
                        <th>Estado</th>
                        <th>Excepción</th>
                        <th>Mensaje</th>
                        <th>Endpoint</th>
                        <th>Traza</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.eventos.map((evento) => (
                        <tr key={evento.id}>
                          <td>{formatDateTime(evento.fechaLocal)}</td>
                          <td>{formatTenantTypeLabel(evento.tipoTenant)}</td>
                          <td>{getTenantEntityName(evento)}</td>
                          <td title={evento.emailUsuario || ''}>{getUserLabel(evento)}</td>
                          <td>{formatRoleLabel(evento)}</td>
                          <td>{evento.nivel || 'error'}</td>
                          <td>{evento.manejado ? 'Manejado' : 'No manejado'}</td>
                          <td>{truncate(evento.exceptionType, 64)}</td>
                          <td>{truncate(evento.mensaje, 120)}</td>
                          <td>{evento.path || evento.categoria || '—'}</td>
                          <td>{evento.traceId || evento.fingerprint || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="panel-empty">No hay errores recientes para mostrar.</div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminGeneralErrors;
